import { useState } from 'react';
import LLMSuggest from './LLMSuggest';

/**
 * A controlled list of short string values, rendered as removable chips with an
 * add-on-Enter input — the pattern already used for project seed labels,
 * discovery constraints and growth-agent seeds.
 *
 * This exists because that pattern had been hand-rolled three separate times
 * (ProjectSettings, ConstraintsEditor, GrowthAgentPanel) and had already drifted:
 * one lowercases entries and the others don't, one adds on blur and the others
 * don't. News Beats needed a fourth copy, which was the point to stop and share
 * one.
 *
 * Pass `suggestContext` to attach the LLM typeahead (the sparkles button + its
 * dropdown). Leave it off for a plain chip input — the constraints editor has no
 * server-side prompt for its lists and shouldn't grow a broken button.
 */
export default function ChipList({
  values,
  onChange,
  placeholder,
  suggestContext,
  suggestLabel,
  additionalContext,
  lowercase = false,
  addOnBlur = true,
  disabled = false,
  inputId,
  clearable = false,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Server-side prompt key from api/autocomplete.py PROMPT_TEMPLATES. */
  suggestContext?: string;
  suggestLabel?: string;
  additionalContext?: Record<string, string>;
  /** Normalize entries to lowercase — right for tags/attribute values, wrong
   *  for anything a human reads back verbatim. */
  lowercase?: boolean;
  addOnBlur?: boolean;
  disabled?: boolean;
  inputId?: string;
  /** Offer a "clear all". Opt-in: on a list that saves as you type, emptying it
   *  is immediate and there is no undo. */
  clearable?: boolean;
}) {
  const [draft, setDraft] = useState('');
  // Two-step rather than a window.confirm: clearing 65 search terms one × at a
  // time is the thing this replaces, so the button has to be quick — but not so
  // quick that a mis-click costs the list.
  const [confirming, setConfirming] = useState(false);

  const add = (raw?: string) => {
    const candidate = (raw ?? draft).trim();
    const next = lowercase ? candidate.toLowerCase() : candidate;
    setDraft('');
    if (!next) return;
    // Case-insensitive duplicate check regardless of `lowercase`: "Solar" and
    // "solar" are one search term either way, and letting both in doubles the
    // query for nothing.
    if (values.some((v) => v.toLowerCase() === next.toLowerCase())) return;
    onChange([...values, next]);
  };

  return (
    <div>
      {values.length > 0 && clearable && (
        <div className="flex justify-end mb-1">
          {confirming ? (
            <span className="flex items-center gap-2 text-xs">
              <span className="text-gray-500 dark:text-gray-400">
                Remove all {values.length}?
              </span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  onChange([]);
                  setConfirming(false);
                }}
                className="text-red-600 dark:text-red-400 hover:underline"
              >
                Yes, clear
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="text-gray-500 dark:text-gray-400 hover:underline"
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              disabled={disabled}
              onClick={() => setConfirming(true)}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
            >
              Clear all ({values.length})
            </button>
          )}
        </div>
      )}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {values.map((val) => (
            <span
              key={val}
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
            >
              {val}
              <button
                type="button"
                disabled={disabled}
                aria-label={`Remove ${val}`}
                onClick={() => onChange(values.filter((x) => x !== val))}
                className="icon-btn text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 disabled:opacity-50"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {/* `relative` is required by LLMSuggest's typeahead variant, which
          absolutely positions its trigger against this box. */}
      <div className="relative">
        <input
          id={inputId}
          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-1.5 text-sm pr-9"
          value={draft}
          disabled={disabled}
          placeholder={placeholder ?? 'Type and press Enter'}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          onBlur={() => {
            if (addOnBlur) add();
          }}
        />
        {suggestContext && (
          <LLMSuggest
            variant="typeahead"
            context={suggestContext}
            label={suggestLabel}
            value={draft}
            multiSelect
            disabled={disabled}
            additionalContext={{
              ...(additionalContext ?? {}),
              existing: values.join(', '),
            }}
            onSelect={(s) => add(s)}
          />
        )}
      </div>
    </div>
  );
}
